import * as THREE from 'three'
import React, {
  forwardRef,
  useEffect,
  useRef,
  useMemo
} from 'react'
import { useGLTF, useTexture } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import { DefectsOnFabric } from './scene/DefectsOnFabric.tsx'

type DefectsProps = {
  fabricRef: React.RefObject<THREE.Mesh>
  isMobile?: boolean
  showBoundingBox?: boolean
}

type ModelProps = {
  largeur?: number
  nbCam?: number
  textile?: 'denim' | 'maille' | 'similicuir'
  isMobile?: boolean
  texture?: string | null
  showBoundingBox?: boolean
  scrollSpeed?: number
  normalScale?: number
  roughness?: number
  anisotropy?: number
}

/* ---------------- CAMERA ITEM ---------------- */

const CameraItem = ({ x, nodes }: any) => {
  const sndAdd = useRef(
    new Audio(`${process.env.PUBLIC_URL}/sounds/cam-add.mp3`)
  )

  useEffect(() => {
    sndAdd.current.currentTime = 0
    sndAdd.current.volume = 0.18
    sndAdd.current.play().catch(() => {})
  }, [])

  const Part = ({ name, color }: { name: string; color?: string }) => {
    const node = nodes[name]
    if (!node) return null
    return (
      <mesh
        geometry={node.geometry}
        material={node.material}
        position={node.position}
        rotation={node.rotation}
        scale={node.scale}
      >
        {color && <meshStandardMaterial color={color} />}
      </mesh>
    )
  }

  return (
    <group name="CameraItem" position={[x, 0, 0]}>
      <Part name="CamBox" />
      <Part name="CamLensMain" color="#58a08c" />
      <Part name="CamFixture" />
      <Part name="CamBackVis" />
      <Part name="CamFrontVis" />
      <Part name="CamCentralHandle" />
      <Part name="CamCMount" />
      <Part name="CamLensInside" />
    </group>
  )
}

/* ---------------- MODEL ---------------- */

export const Model = forwardRef<any, ModelProps>((props, ref) => {
  const {
    largeur = 180,
    nbCam = 1,
    textile = 'denim',
    isMobile = false,
    texture = null,
    showBoundingBox = false,
    scrollSpeed = 1,
    normalScale = 1,
    roughness = 0.35,
    anisotropy = 8
  } = props

  const { nodes } = useGLTF(
    `${process.env.PUBLIC_URL}/ViZmeraldSolution.glb`
  ) as any

  /* ---------------- DIMENSIONS ---------------- */

  const largeurM = largeur / 100
  const offsetMetre = (largeur - 180) / 100

  const sCap = (largeurM + 0.18) / 1.98
  const sBar = (largeurM + 0.18) / 2.0
  const sFabric = (largeurM + 0.18) / 2.06
  const sBottom = (largeurM + 0.35) / 2.14
  const sFloor = (largeurM + 1.2) / 3.0

  /* ---------------- AUDIO REMOVE ---------------- */

  const sndRemove = useRef(
    new Audio(`${process.env.PUBLIC_URL}/sounds/cam-remove.mp3`)
  )
  const prevNbCam = useRef(nbCam)

  useEffect(() => {
    if (nbCam < prevNbCam.current) {
      sndRemove.current.currentTime = 0
      sndRemove.current.volume = 0.3
      sndRemove.current.play().catch(() => {})
    }
    prevNbCam.current = nbCam
  }, [nbCam])

  /* ---------------- TEXTURE PATHS ---------------- */

  const diffusePath = useMemo(() => {
    if (texture)
      return `${process.env.PUBLIC_URL}/textures/${texture}`
    if (textile === 'denim')
      return `${process.env.PUBLIC_URL}/textures/7914.jpg`
    if (textile === 'maille')
      return `${process.env.PUBLIC_URL}/textures/12722.jpg`
    return `${process.env.PUBLIC_URL}/textures/11081.jpg`
  }, [texture, textile])

  const SHARED_NORMALS = [
  '283-normal.jpg',
  '4538-normal.jpg',
  '4977-normal.jpg',
  '6741-normal.jpg',
  '7786-normal.jpg',
  '7914-normal.jpg',
  '7998-normal.jpg',
  '9297-normal.jpg',
  '9471-normal.jpg',
  '11081-normal.jpg',
  '12722-normal.jpg',
  '16528-normal.jpg'
]

function pickStableNormal(seed: string) {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i)
    hash |= 0
  }
  const index = Math.abs(hash) % SHARED_NORMALS.length
  return SHARED_NORMALS[index]
}

  const normalPath = useMemo(() => {
  // cas textiles connus
  if (textile === 'denim')
    return `${process.env.PUBLIC_URL}/textures/7914-normal.jpg`
  if (textile === 'maille')
    return `${process.env.PUBLIC_URL}/textures/12722-normal.jpg`
  if (textile === 'similicuir')
    return `${process.env.PUBLIC_URL}/textures/11081-normal.jpg`

  // texture custom → normal random STABLE
  if (texture) {
    const normalName = pickStableNormal(texture)
    return `${process.env.PUBLIC_URL}/textures/${normalName}`
  }

  // fallback ultime (sécurité)
  return `${process.env.PUBLIC_URL}/textures/7914-normal.jpg`
}, [texture, textile])

  /* ---------------- LOAD TEXTURES (STABLE) ---------------- */

  const [activeMap, activeNormal] = useTexture(
    [diffusePath, normalPath],
    (textures) => {
      const [map, normal] = textures

      map.encoding = THREE.sRGBEncoding
      map.wrapS = map.wrapT = THREE.RepeatWrapping
      map.anisotropy = anisotropy
      map.flipY = false
      map.repeat.set(sFabric, 1)

      normal.encoding = THREE.LinearEncoding
      normal.wrapS = normal.wrapT = THREE.RepeatWrapping
      normal.anisotropy = anisotropy
      normal.flipY = false
      normal.repeat.set(sFabric, 1)
    }
  )

  /* ---------------- ANIMATE TEXTURE ---------------- */

  useFrame((state) => {
    const movement = -state.clock.getElapsedTime() * scrollSpeed
    if (activeMap?.image) activeMap.offset.y = movement
    if (activeNormal?.image) activeNormal.offset.y = movement
  })

  /* ---------------- CAM POSITIONS ---------------- */

  const camPositions = useMemo(() => {
    const margeBord = 0.25
    const espaceUtile = largeurM - margeBord * 2
    return Array.from({ length: nbCam }).map((_, i) =>
      nbCam > 1
        ? -margeBord - i * (espaceUtile / (nbCam - 1))
        : -(largeurM / 2)
    )
  }, [nbCam, largeurM])

  /* ---------------- STATIC PART ---------------- */

  const StaticPart = ({ name, scaleX = 1 }: { name: string; scaleX?: number }) => {
    const node = nodes[name]
    if (!node) return null
    return (
      <mesh
        geometry={node.geometry}
        material={node.material}
        position={node.position}
        rotation={node.rotation}
        scale={[node.scale.x * scaleX, node.scale.y, node.scale.z]}
      />
    )
  }

  const fabricRef = useRef<THREE.Mesh>(null!)

  /* ---------------- MEMO MATERIAL ---------------- */

  const fabricMaterial = useMemo(
    () => (
      <meshStandardMaterial
        map={activeMap}
        normalMap={activeNormal}
        normalScale={new THREE.Vector2(normalScale, normalScale)}
        roughness={roughness}
        metalness={0.1}
      />
    ),
    [activeMap, activeNormal, normalScale, roughness]
  )

  /* ---------------- RENDER ---------------- */

  return (
    <group ref={ref} dispose={null}>
      <group name="MainWrapper" position={[offsetMetre / 2 + 0.5, 0, 0]}>
        <group name="Cote_Droit">
          <StaticPart name="Base_R" />
          <StaticPart name="cap_R" />
          <StaticPart name="SupportMetal_R" />
          <StaticPart name="SupportPlast_R" />
        </group>

        <group name="Partie_Centrale">
          {nodes.Cap_Top && (
            <mesh
              geometry={nodes.Cap_Top.geometry}
              scale={[sCap, 1, 1]}
              position={nodes.Cap_Top.position}
            >
              <meshStandardMaterial
                color="#ffffff"
                transparent
                opacity={0.4}
                roughness={0.1}
                metalness={0.3}
              />
            </mesh>
          )}

          <StaticPart name="centralbar" scaleX={sBar} />
          <StaticPart name="CentralCylinder" scaleX={sBar} />
          <StaticPart name="centralBottomBar" scaleX={sBottom} />
          <StaticPart name="Floor" scaleX={sFloor} />
          {nodes.EstradeBas && (
            <StaticPart name="EstradeBas" scaleX={sBar} />
          )}

          {nodes.Fabric && (
            <mesh
              ref={fabricRef}
              geometry={nodes.Fabric.geometry}
              scale={[
                nodes.Fabric.scale.x * sFabric,
                nodes.Fabric.scale.y,
                nodes.Fabric.scale.z
              ]}
              position={nodes.Fabric.position}
            >
              {fabricMaterial}
            </mesh>
          )}

          <DefectsOnFabric
            fabricRef={fabricRef}
            isMobile={isMobile}
            showBoundingBox={showBoundingBox}
          />
        </group>

        <group name="Cote_Gauche" position-x={-offsetMetre}>
          <StaticPart name="Base_L" />
          <StaticPart name="cap_L" />
          <StaticPart name="SupportMetal_L" />
          <StaticPart name="SupportPlast_L" />
        </group>

        {camPositions.map((pos, i) => (
          <CameraItem key={`cam-${i}`} x={pos} nodes={nodes} />
        ))}
      </group>
    </group>
  )
})

Model.displayName = 'Model'
useGLTF.preload(`${process.env.PUBLIC_URL}/ViZmeraldSolution.glb`)
